import useNavigationStore from '@src/stores/navigationStore';

import styles from './Signature.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import Loader from '@src/components/_shared/UI/Loader';
import useRegistrationStore from '@src/stores/registrationStore';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sendSignatureOTP, signContract, uploadContract, conventionData } from '@src/api/Registration';
import SignatureModal from '@src/components/_shared/SignatureModal';
import { Accordion, AccordionDetails, AccordionSummary, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import Warning from '@src/assets/Warning.png';
import CheckOK from '@src/assets/CheckOK.png';
import PDFViewer from '@src/components/_shared/UI/PDFViewer';
import Danger from '@src/assets/Danger.png';
import { generateTosPdf } from '@src/api/User';
import BackButton from '@src/components/_shared/BackButton';
import { getReferencialData } from '@src/api/Referencial';
import { extractPhoneNumber, handleApiError } from '@src/utilities/functions';
import Refresh from '@src/assets/Refresh.png';

export default function Signature() {
    const { setSection, setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();
    const [pdfLoadingStates, setPdfLoadingStates] = useState<boolean[]>([true, true]);
    const [showCodeModal, setShowCodeModal] = useState<boolean>(false);
    const [countryCodeArray, setCountryCodeArray] = useState<string[]>([]);
    const { demand_id, person_id, partner_informations: { is_partner, brand: { slug, sitecode } } } = registration;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [contractPdfUrl, setContractPdfUrl] = useState<string | null>(null);
    const [openedAccordions, setOpenedAccordions] = useState<boolean[]>(Array(6).fill(false));
    const [accordionViewHistory, setAccordionViewHistory] = useState<boolean[]>(Array(6).fill(false));

    const { data, isLoading } = useQuery({
        queryKey: ["getGeneratePdfData"],
        queryFn: () => generateTosPdf(),
        enabled: !is_partner
    })

    const { isSuccess: isConventionSuccess, isLoading: isConventionLoading, refetch: refetchConvention, isError: isConventionError } = useQuery({
        queryKey: ["contractPdfBlob"],
        queryFn: async () => {
            try {
                const result = await conventionData({ demand_id, is_psi: is_partner, site_code: sitecode });
                setContractPdfUrl(result.blobUrl);
                return result;
            } catch (error) {
                console.error('Error fetching convention PDF:', error);
                updatePdfLoadingState(0, false);
            }
        }
    });

    useEffect(() => {
        return () => {
            if (contractPdfUrl) {
                URL.revokeObjectURL(contractPdfUrl);
            }
        };
    }, [contractPdfUrl]);

    useEffect(() => {
        if (!isConventionLoading && (isConventionSuccess || isConventionError)) {
            updatePdfLoadingState(0, false);
        }
    }, [isConventionLoading, isConventionSuccess, isConventionError]);

    const { data: countriesData, isLoading: isLoadingCountriesData } = useQuery({
        queryKey: [`getCountriesData`],
        queryFn: () => getReferencialData({type: 'countries'})
    })

    useEffect(() => {
        if (countriesData) {
            setCountryCodeArray(countriesData.map((obj: { phone_extension: string; }) => {return obj.phone_extension}))
        }
    }, [countriesData]);

    const PDF_URLS = {
        BOURSE_DIRECT: {
            POLICY_EXECUTION: 'https://www.boursedirect.fr/pdf/politiqueExeMif.pdf',
            MIFID_LETTER: 'https://www.boursedirect.fr/pdf/Lettre_Categorisation_Client.pdf',
            CONFLICT_POLICY: 'https://www.boursedirect.fr/pdf/Resume_politique_gestion_des_conflits_interets.pdf'
        },
        DIRECT_SECURITIES: {
            GENERAL_CONDITIONS: 'https://www.directsecurities.fr/pdf/DScgDirect.pdf',
            POLICY_EXECUTION: 'https://www.directsecurities.fr/pdf/politiqueExeMif_DS.pdf',
            MIFID_LETTER: 'https://www.directsecurities.fr/pdf/DS_Lettre_Categorisation_Client.pdf',
            CONFLICT_POLICY: 'https://www.directsecurities.fr/pdf/DS_Resume_politique_gestion_des_conflits_interets.pdf'
        },
    }

    const pdfData = [
        {
            id: 1,
            title: 'Dossier pré-rempli',
            name: 'contractPDF',
            url: contractPdfUrl || ''
        },
        {
            id: 2,
            title: 'Conditions générales',
            name: 'otherPDF',
            url: is_partner ? PDF_URLS.DIRECT_SECURITIES.GENERAL_CONDITIONS : `${data?.url}`
        },
        {
            id: 3,
            title: 'Politique d\'éxecution',
            name: 'otherPDF',
            url: is_partner ? PDF_URLS.DIRECT_SECURITIES.POLICY_EXECUTION : PDF_URLS.BOURSE_DIRECT.POLICY_EXECUTION
        },
        {
            id: 4,
            title: 'Lettre d\'information MIFID',
            name: 'otherPDF',
            url: is_partner ? PDF_URLS.DIRECT_SECURITIES.MIFID_LETTER : PDF_URLS.BOURSE_DIRECT.MIFID_LETTER
        },
        {
            id: 5,
            title: 'Politique conflit d\'intérêts',
            name: 'otherPDF',
            url: is_partner ? PDF_URLS.DIRECT_SECURITIES.CONFLICT_POLICY : PDF_URLS.BOURSE_DIRECT.CONFLICT_POLICY
        },
        {
            id: 6,
            title: 'CGU signature électronique',
            name: 'otherPDF',
            url: `/hub/registration/e-sign/eSign-tos`
        }
    ]

    const allAccordionsViewed = accordionViewHistory.every((isViewed) => isViewed);

    const { mutate: uploadContractMutate, isPending: isLoadingUploadContract} = useMutation({
        mutationFn: () => uploadContract(is_partner ? {is_psi: is_partner, demand_id, person_id, brand: slug} : {}),
        onSuccess: () => {
            sendOtpMutate()
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: sendOtpMutate, isPending: isPendingSendOtp} = useMutation({
        mutationFn: () => sendSignatureOTP({demand_id, is_psi: is_partner, person_id, brand: slug}),
        onSuccess: () => {
            setShowCodeModal(true)
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: signContractMutate, isPending: isPendingSignContract} = useMutation({
        mutationFn: (otp_code: string) => signContract({otp_code, demand_id, is_psi: is_partner, person_id, brand: slug}),
        onSuccess: () => {
            setShowCodeModal(false);
            setSection(7);
            setBlock('Finish')
        },
        onError: (error: any) => {
            if (error.response.status === 400) {
                toast.error('Le code saisi n\'est pas correct', { position: 'top-center' });
            } else {
                handleApiError(error)
            }
        }
    })

    const _onCLoseModal = () => {
        setShowCodeModal(false);
    }

    const updatePdfLoadingState = (index: number, isLoading: boolean) => {
        setPdfLoadingStates((prev) => {
            const newState = [...prev];
            newState[index] = isLoading;
            return newState;
        });
    };

    const handleAccordionChange = (index: number, isExpanded: boolean) => {
        setOpenedAccordions((prev) => {
            const updated = [...prev];
            updated[index] = isExpanded;
            return updated;
        });
        if (isExpanded) {
            setAccordionViewHistory((prev) => {
                const updated = [...prev];
                updated[index] = true;
                return updated;
            });
        }
    };

    const getAccordionIcon = (isLoading: boolean, isOpened: boolean, isError?: boolean) => {
        if (isLoading) {
            return <Loader type="SMALL_SPINNER" />;
        }

        if (isError) {
            return (
                <div className={styles.icon}>
                    <img src={Danger} alt="Error Icon" style={{ width: '22px', height: '22px' }} />
                </div>
            );
        }
        const iconSrc = isOpened ? CheckOK : Warning;
        const iconStyles = {
            width: '22px',
            height: isOpened ? '16px' : '22px',
        };
        return (
            <div className={styles.icon}>
                <img src={iconSrc} alt="Status Icon" style={iconStyles} />
            </div>
        );
    }

    if (isLoading || isLoadingCountriesData) return (
        <Container>
            <Loader type="SPINNER" />
        </Container>
    );
    return (
        <Container>
            <BackButton onClick={() => setBlock('Overview')}/>
            <BlockTitle title="Signature du contrat" />
            <Subtitle content="Veuillez relire et vérifier les informations relatives à la convention, avant de signer." />
            {pdfData.map((item, index) => (
                <div key={item.id} style={{ position: 'relative' }}>
                    <Accordion
                        style={{
                            marginTop: item.id === 1 ? '40px' : '0',
                            marginBottom: item.id === pdfData.length ? '20px' : '0',
                            width: isMobile || isTablet ? '100%' : '800px'
                        }}
                        sx={{
                            boxShadow: 'none !important',
                            '&:before': {
                                display: 'none'
                            }
                        }}
                        onChange={(_, isExpanded) => {
                            if (item.name === 'contractPDF' && isConventionError) {
                                return;
                            } else {
                                handleAccordionChange(index, isExpanded);
                            }
                        }}
                        expanded={openedAccordions[index]}
                    >
                        <AccordionSummary
                            expandIcon={!pdfLoadingStates[index] && !(item.name === 'contractPDF' && isConventionError) && <Typography sx={{ fontSize: '13px' }}>▼</Typography>}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                            sx={{ backgroundColor: '#f1f1f1', borderRadius: '10px', marginBottom: '7px', cursor: (pdfLoadingStates[index] || (item.name === 'contractPDF' && isConventionLoading)) ? 'not-allowed' : 'pointer' }}
                            disabled={pdfLoadingStates[index] || (item.name === 'contractPDF' && isConventionLoading)}
                        >
                            {getAccordionIcon(pdfLoadingStates[index], accordionViewHistory[index], item.name === 'contractPDF' && !isConventionSuccess && !isConventionLoading)}
                            <Typography component="span" sx={{ fontWeight: 600, fontSize: 16, margin: 'auto 0' }}>{item.title}</Typography>
                            {item.name === 'contractPDF' && !isConventionSuccess && !isConventionLoading && (
                                <>
                                    <Typography 
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 12,
                                            marginLeft: 'auto',
                                            marginRight: '30px',
                                            marginTop: 'auto',
                                            marginBottom: 'auto',
                                            color: 'rgb(228, 31, 64)'
                                        }}
                                    >
                                        Erreur lors du chargement du contrat
                                    </Typography>
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            refetchConvention();
                                            updatePdfLoadingState(index, true);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: 'auto',
                                            zIndex: 10,
                                            minWidth: 'auto',
                                            backgroundColor: '#f1f1f1',
                                            color: '#333',
                                            '&:hover': {
                                                backgroundColor: '#e0e0e0',
                                            },
                                        }}
                                    >
                                        <img src={Refresh} alt="Refresh" style={{ width: '20px', height: '20px' }} />
                                    </Button>
                                </>
                            )}
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: '#f1f1f1', borderRadius: '10px', marginBottom: '7px'}}>
                            <PDFViewer
                                name={item.name}
                                setIsLoading={(isLoading) =>
                                    updatePdfLoadingState(index, isLoading)
                                }
                                url={item.url}
                                isLoading={pdfLoadingStates[index]}
                            />
                        </AccordionDetails>
                    </Accordion>
                </div>
            ))}
            <ValidateButton title='Signer ma convention' onClick={() => uploadContractMutate()} disabled={pdfLoadingStates.some((state) => state) || !allAccordionsViewed} onLoading={isLoadingUploadContract || isPendingSendOtp}/>
            <SignatureModal
                show={showCodeModal}
                onClose={() => _onCLoseModal()}
                onValidate={(code: string) => signContractMutate(code)}
                retrySendCode={() => sendOtpMutate()}
                isLoading={isPendingSendOtp || isPendingSignContract}
                phoneNumber={extractPhoneNumber(countryCodeArray, registration.person.basic.phone).extractPhone}
            />            
        </Container>
    );
}
